import { useState } from 'react';
import toast from 'react-hot-toast';
import { Mail, MessageSquare, User, Send, MapPin, Clock } from 'lucide-react';
import emailjs from '@emailjs/browser';

const EMAILJS_SERVICE_ID  = 'service_8l03p9t';
const EMAILJS_TEMPLATE_ID = 'template_bj2wrzc';
const EMAILJS_PUBLIC_KEY  = 'KDenUyK-9peMJL90g';


const ContactPage = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);


  const handleSubmit = async (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error('Please enter your name!');
      return;
    }
    if (!form.email.trim()) {
      toast.error('Please enter your email!');
      return;
    }
    if (!form.message.trim()) {
      toast.error('Please enter a message!');
      return;
    }

    setLoading(true);

    try {
      // Send via EmailJS ✅
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name:  form.name,
          from_email: form.email,
          subject:    form.subject ||
                      'General Inquiry',
          message:    form.message,
          to_email:   'support@scaffoldbridge.io'
        },
        EMAILJS_PUBLIC_KEY
      );

      setSent(true);
      toast.success(
        'Message sent! We\'ll reply soon 🎉');
      setForm({
        name: '', email: '',
        subject: '', message: ''
      });

    } catch (err) {
      console.error('EmailJS error:', err);
      toast.error(
        'Failed to send. Please email us at ' +
        'support@scaffoldbridge.io');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 py-16 px-4">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="text-center mb-14">
          <div className="text-blue-400 font-mono text-sm mb-3">
            // Get in touch
          </div>
          <h1 className="text-4xl font-bold text-blue-200 mb-4">
            Contact Us
          </h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Have a question, feedback or need help?
            We'd love to hear from you. Send us a message
            and we'll respond as soon as possible.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">

          {/* ── Left — Contact Info ── */}
          <div className="space-y-6">

            {/* Info Cards */}
            {[
              {
                icon: <Mail className="w-5 h-5 text-blue-400" />,
                title: 'Email Us',
                value: 'support@scaffoldbridge.io',
                sub: 'We reply within 24 hours'
              },
              {
                icon: <Clock className="w-5 h-5 text-blue-400" />,
                title: 'Response Time',
                value: 'Within 24 hours',
                sub: 'Monday to Friday'
              },
              {
                icon: <MapPin className="w-5 h-5 text-blue-400" />,
                title: 'Based In',
                value: 'United States',
                sub: 'Serving developers worldwide'
              }
            ].map(item => (
              <div key={item.title}
                   className="bg-gray-900 border border-gray-800
                              rounded-2xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500/10 border
                                border-blue-500/20 rounded-xl
                                flex items-center justify-center
                                flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <p className="text-gray-500 text-xs font-mono
                                uppercase tracking-wider mb-1">
                    {item.title}
                  </p>
                  <p className="text-gray-200 text-sm font-medium">
                    {item.value}
                  </p>
                  <p className="text-gray-600 text-xs mt-0.5">
                    {item.sub}
                  </p>
                </div>
              </div>
            ))}

            {/* FAQ hint */}
            <div className="bg-blue-500/5 border border-blue-500/20
                            rounded-2xl p-5">
              <p className="text-blue-400 text-sm font-semibold mb-2">
                💡 Quick Help
              </p>
              <p className="text-gray-500 text-xs leading-relaxed">
                For bug reports, please include your browser,
                OS, and steps to reproduce. For feature requests,
                describe your use case clearly.
              </p>
            </div>
          </div>

          {/* ── Right — Contact Form ── */}
          <div className="lg:col-span-2">
            <div className="bg-gray-900 border border-gray-800
                            rounded-2xl p-8">

              <div className="flex items-center gap-2 mb-6">
                <MessageSquare className="w-4 h-4 text-blue-400" />
                <h2 className="text-blue-200 font-semibold">
                  Send a Message
                </h2>
              </div>

              <form onSubmit={handleSubmit}
                    className="space-y-5">

                {/* Name + Email row */}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-gray-500 text-xs
                                      font-mono uppercase tracking-wider mb-2">
                      Your Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2
                                       -translate-y-1/2 w-4 h-4
                                       text-gray-600" />
                      <input
                        type="text"
                        value={form.name}
                        onChange={e => setForm({
                          ...form, name: e.target.value
                        })}
                        placeholder="John Doe"
                        className="w-full bg-gray-800 border border-gray-700
                                   text-gray-200 rounded-xl pl-10 pr-4 py-3
                                   text-sm outline-none focus:border-blue-500
                                   transition-colors placeholder-gray-600"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-500 text-xs
                                      font-mono uppercase tracking-wider mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2
                                       -translate-y-1/2 w-4 h-4
                                       text-gray-600" />
                      <input
                        type="email"
                        value={form.email}
                        onChange={e => setForm({
                          ...form, email: e.target.value
                        })}
                        placeholder="john@company.com"
                        className="w-full bg-gray-800 border border-gray-700
                                   text-gray-200 rounded-xl pl-10 pr-4 py-3
                                   text-sm outline-none focus:border-blue-500
                                   transition-colors placeholder-gray-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-gray-500 text-xs
                                    font-mono uppercase tracking-wider mb-2">
                    Subject
                  </label>
                  <select
                    value={form.subject}
                    onChange={e => setForm({
                      ...form, subject: e.target.value
                    })}
                    className="w-full bg-gray-800 border border-gray-700
                               text-gray-200 rounded-xl px-4 py-3 text-sm
                               outline-none focus:border-blue-500
                               transition-colors">
                    <option value="">Select a subject...</option>
                    <option value="bug">🐛 Bug Report</option>
                    <option value="feature">💡 Feature Request</option>
                    <option value="billing">💳 Billing Question</option>
                    <option value="general">💬 General Question</option>
                    <option value="other">📋 Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-gray-500 text-xs
                                    font-mono uppercase tracking-wider mb-2">
                    Message *
                  </label>
                  <textarea
                    value={form.message}
                    onChange={e => setForm({
                      ...form, message: e.target.value
                    })}
                    placeholder="Describe your question or issue in detail..."
                    rows={6}
                    className="w-full bg-gray-800 border border-gray-700
                               text-gray-200 rounded-xl px-4 py-3 text-sm
                               outline-none focus:border-blue-500
                               transition-colors resize-none
                               placeholder-gray-600"
                  />
                  <p className="text-gray-600 text-xs mt-1 text-right">
                    {form.message.length} characters
                  </p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2
                             bg-blue-600 hover:bg-blue-500 text-white
                             font-bold py-4 rounded-xl transition-all
                             hover:shadow-lg hover:shadow-blue-500/25
                             disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2
                                      border-white border-t-transparent
                                      rounded-full"/>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;